import type { ActiveAlert, OverviewPool, PoolDef, Snapshot } from '../../shared/types';
import { fmtCompact, fmtPct, fmtSignedPct } from '../../shared/format';
import { Sparkline } from './Sparkline';

type Props = { item: OverviewPool; alerts: ActiveAlert[] };

const TIER_LABEL: Record<PoolDef['tier'], string> = { 1: 'Tier 1', 2: 'Tier 2', 3: 'Tier 3' };

export function PoolCard({ item, alerts }: Props) {
  const { pool, latest, series } = item;
  const state = alerts.some((a) => a.severity === 'critical') ? 'critical' : alerts.length ? 'warn' : 'ok';
  return (
    <article class={`pool pool--${state}${pool.watchOnly ? ' pool--watch' : ''}`}>
      <div class="pool-head">
        <h2 class="pool-title">
          <a href={pool.url} target="_blank" rel="noreferrer noopener">{pool.name}</a>
        </h2>
        <span class="pool-meta mono">{pool.watchOnly ? 'Watch only · ' : ''}{TIER_LABEL[pool.tier]} · {pool.asset}</span>
      </div>

      {latest ? (
        <>
          <div class="pool-main">
            <div>
              <div class="pool-value mono">{fmtCompact(latest.availableLiquidity)}</div>
              <div class="pool-value-label mono">available to exit</div>
            </div>
            <Sparkline class="spark" values={series.map((p) => p.availableLiquidity)} />
          </div>
          {alerts.length > 0 && (
            <div class="pool-flags">
              {alerts.map((a) => (
                <span key={a.ruleKey} class={`pill pill--${a.severity}`} title={a.detail}>{a.title}</span>
              ))}
            </div>
          )}
          <div class="pool-deltas">
            <Delta label="24H" v={item.liquidityDelta24h} />
            <Delta label="7D" v={item.liquidityDelta7d} />
          </div>
          <dl class="pool-stats mono">
            {statsFor(pool, latest).map(([k, v]) => (
              <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
            ))}
          </dl>
        </>
      ) : (
        <p class="dim">No snapshot yet.</p>
      )}
    </article>
  );
}

function Delta({ label, v }: { label: string; v: number | null }) {
  const cls = v === null ? 'flat' : v > 0.001 ? 'gain' : v < -0.001 ? 'loss' : 'flat';
  return (
    <div class="pool-delta">
      <span class="pool-delta-label mono">{label}</span>
      <span class={`pool-delta-value mono ${cls}`}>{fmtSignedPct(v)}</span>
    </div>
  );
}

function statsFor(pool: PoolDef, s: Snapshot): [string, string][] {
  const x = s.extra as Record<string, unknown>;
  const num = (k: string) => (typeof x[k] === 'number' ? (x[k] as number) : null);
  const rows: [string, string][] = [];
  switch (pool.kind) {
    case 'aave-v3':
    case 'compound-v3':
    case 'morpho-market': {
      const target = num('optimalRatio') ?? num('supplyKink') ?? pool.utilizationTarget ?? null;
      rows.push(['Utilization', `${fmtPct(s.utilization)}${target !== null ? ` · target ${fmtPct(target, 0)}` : ''}`]);
      rows.push(['Supplied', fmtCompact(s.totalSupplied)]);
      rows.push(['Supply APY', fmtPct(s.supplyApy, 2)]);
      if (pool.kind === 'compound-v3') rows.push(['Reserves', `${fmtCompact(num('reserves'))} / ${fmtCompact(num('targetReserves'))}`]);
      if (pool.kind === 'morpho-market') rows.push(['LLTV', fmtPct(num('lltv'), 0)]);
      break;
    }
    case 'morpho-vault': {
      const allocs = Array.isArray(x.allocations) ? (x.allocations as unknown[]).length : 0;
      rows.push(['Utilization', `${fmtPct(s.utilization)} · target ${fmtPct(pool.utilizationTarget ?? null, 0)}`]);
      rows.push(['Vault assets', fmtCompact(s.totalSupplied)]);
      rows.push(['Net APY', fmtPct(s.supplyApy, 2)]);
      rows.push(['Timelock', `${Math.round((num('timelock') ?? 0) / 86400)}d · ${allocs} markets`]);
      break;
    }
    case 'liquity-sp': {
      rows.push(['SP coverage', `${fmtPct(num('spCoverage'), 0)} of branch debt`]);
      rows.push(['Branch TCR', `${(num('tcr') ?? 0).toFixed(2)} · CCR ${(pool.params?.ccr ?? 0).toFixed(2)}`]);
      rows.push(['SP yield APR', fmtPct(s.supplyApy, 2)]);
      if (s.price !== null) rows.push(['BOLD price', `$${s.price.toFixed(4)}`]);
      if (num('curveTvl') !== null) rows.push(['Curve BOLD/USDC', fmtCompact(num('curveTvl'))]);
      break;
    }
    case 'maple-syrup': {
      const c = x.concentration as { borrowers?: number; largestLoanShare?: number | null; top3Share?: number | null } | null;
      rows.push(['Lent out', `${fmtPct(s.utilization)} of ${fmtCompact(s.totalSupplied)}`]);
      rows.push(['Supply APY', fmtPct(s.supplyApy, 2)]);
      if (c) rows.push(['Borrowers', `${c.borrowers ?? '—'} · top loan ${fmtPct(c.largestLoanShare ?? null, 0)} · top 3 ${fmtPct(c.top3Share ?? null, 0)}`]);
      rows.push(['Unrealized losses', fmtCompact(s.badDebt)]);
      break;
    }
    case 'eth-gas':
      break;
  }
  return rows;
}
